/**
 * event-bus.js
 *
 * Unified event bus for the LoveGroomers theme.
 * Replaces the mix of jQuery .trigger() magic strings with typed, namespaced constants.
 *
 * Usage:
 *   import { bus, Events } from './event-bus';
 *
 *   // Emit
 *   bus.emit(Events.CART_UPDATED, { quantity: 3 });
 *
 *   // Listen (returns an unsubscribe function)
 *   const off = bus.on(Events.CART_UPDATED, e => console.log(e.detail.quantity));
 *   off(); // removes the listener
 *
 *   // Listen on a specific element target
 *   bus.on(Events.COLLAPSIBLE_OPEN, handler, myElement);
 *
 * What stays out of this bus:
 *   - Stencil hooks (platform API — facetedSearch-*, sortBy-submitted, product-option-change)
 *   - Foundation events (closed.fndtn.dropdown etc.)
 *   - Native lifecycle events (DOMContentLoaded, beforeunload, visibilitychange)
 *   - window.dataLayer pushes (GTM contract, must not change format)
 */

export const Events = {
    // ── Cart ──────────────────────────────────────────────────────────────────
    /** Fired after cart quantity changes. detail: { quantity: number } */
    CART_UPDATED: 'lg:cart-updated',
    /** Fired after a Quick Add by SKU form adds to the cart. No detail. */
    QUICK_ADD_SKU_ADDED: 'lg:quick-add-sku-added',

    // ── Navigation / Search ───────────────────────────────────────────────────
    /** Fired after a pushState URL change (facets, sort, pagination). */
    STATE_CHANGE: 'lg:state-change',

    // ── Collapsible UI ────────────────────────────────────────────────────────
    /** Fired on the toggle element when a collapsible opens. detail: { instance } */
    COLLAPSIBLE_OPEN: 'lg:collapsible-open',
    /** Fired on the toggle element when a collapsible closes. detail: { instance } */
    COLLAPSIBLE_CLOSE: 'lg:collapsible-close',
    /** Fired on the toggle element on every open or close. detail: { instance } */
    COLLAPSIBLE_TOGGLE: 'lg:collapsible-toggle',
    /** Internal click event on the collapsible toggle. */
    COLLAPSIBLE_CLICK: 'lg:collapsible-click',

    // ── Recommendations ───────────────────────────────────────────────────────
    /** Fired on the carousel container when SearchSpring reco carousel is ready. detail: { container, carousel } */
    RECO_READY: 'lg:reco-ready',

    // ── Product ───────────────────────────────────────────────────────────────
    /** Fired by product-details updateView after an option change. detail: { variantId: number|null, sku: string|null } (V3 variant id) */
    PRODUCT_VARIANT_CHANGED: 'lg:product-variant-changed',
};

class EventBus {
    /**
     * Register a listener for an event.
     * @param {string} event - One of the Events constants.
     * @param {Function} handler - Callback (receives a CustomEvent).
     * @param {EventTarget} [target=document] - DOM target to listen on.
     * @returns {Function} Unsubscribe function — call it to remove the listener.
     */
    on(event, handler, target = document) {
        target.addEventListener(event, handler);
        return () => target.removeEventListener(event, handler);
    }

    /**
     * Remove a listener for an event.
     * @param {string} event
     * @param {Function} handler - Must be the same reference passed to on().
     * @param {EventTarget} [target=document]
     */
    off(event, handler, target = document) {
        target.removeEventListener(event, handler);
    }

    /**
     * Dispatch an event.
     * @param {string} event - One of the Events constants.
     * @param {object} [detail={}] - Payload accessible via e.detail.
     * @param {EventTarget} [target=document] - DOM target to dispatch on.
     */
    emit(event, detail = {}, target = document) {
        target.dispatchEvent(new CustomEvent(event, { detail, bubbles: true }));
    }
}

export const bus = new EventBus();
